/**
 * Locais predefinidos onde os botões customizados podem ser exibidos.
 * Cada valor corresponde a um ponto de entrada no backend (PGCA010).
 */
export enum PgcCustomButtonLocation {
  /** Tela de Necessidade de Compras */
  PURCHASE_REQUESTS = 'purchaseRequests',
  /** Tela de Cotações */
  QUOTATIONS = 'quotations'
}

/**
 * Momento de execução dos botões customizados em relação ao carregamento da listagem.
 */
export enum PgcCustomButtonTiming {
  /**
   * Antes de carregar a listagem.
   * O sistema abre um modal com as opções do PE, e a ação executada pode retornar
   * filtros customizados que serão aplicados na listagem.
   */
  BEFORE = 'before',
  /**
   * Depois que a listagem já foi carregada.
   * Os botões são exibidos inline na interface e executam ações sobre os dados já listados.
   */
  AFTER = 'after'
}

/**
 * Definição de um botão customizado retornado pelo backend.
 */
export interface PgcCustomButton {
  /** Identificador único do botão */
  id: string;
  /** Texto exibido no botão */
  label: string;
  /** Ícone do botão (classe PO-UI / Animalia, ex: 'an an-gear') */
  icon?: string;
  /** Tipo visual do botão (default, primary, secondary, tertiary, danger) */
  kind?: string;
  /** Indica se o botão deve ficar desabilitado */
  disabled?: boolean;
  /** Tooltip informativo */
  tooltip?: string;
  /** Identificador da função customizada a ser executada no backend */
  action: string;
  /** Descrição adicional exibida no modal (modo before) */
  description?: string;
}

/**
 * Resposta da API ao consultar os botões disponíveis para uma localização e timing.
 */
export interface PgcCustomButtonsResponse {
  buttons: PgcCustomButton[];
}

/**
 * Payload enviado ao backend ao executar uma ação de botão customizado.
 */
export interface PgcCustomButtonActionPayload {
  /** Identificador da ação/função customizada */
  action: string;
  /** Local de onde o botão foi acionado */
  location: PgcCustomButtonLocation;
  /** Momento de execução (before/after) */
  timing: PgcCustomButtonTiming;
  /** Contexto da operação (dados da cotação, SC selecionada, etc.) */
  context?: Record<string, any>;
}

/**
 * Resposta da API após a execução de uma ação customizada.
 * Quando timing=before, o campo `filters` pode conter filtros customizados a serem
 * aplicados na listagem antes de carregar os dados.
 */
export interface PgcCustomButtonActionResponse {
  /** Indica se a execução foi bem-sucedida */
  success: boolean;
  /** Mensagem a ser exibida ao usuário */
  message: string;
  /** Filtros customizados para aplicar na listagem (usado no timing=before) */
  filters?: PgcCustomButtonFilter;
  /** Dados adicionais retornados pela execução (opcional) */
  data?: Record<string, any>;
}

/**
 * Filtros customizados retornados pelo PE no modo before.
 * Permitem ao cliente controlar quais dados serão carregados na listagem.
 */
export interface PgcCustomButtonFilter {
  /** Query FILTER no formato OData a ser adicionada na consulta (ex: "c1_produto eq 'PROD01'") */
  filterQuery?: string;
  /** Data início para filtro de período (formato YYYYMMDD) */
  startDate?: string;
  /** Data fim para filtro de período (formato YYYYMMDD) */
  endDate?: string;
  /** Valor de busca textual (search) */
  search?: string;
  /** Parâmetros extras livres para uso do PE */
  extra?: Record<string, any>;
}
